import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { Loader } from "@themes/pollinator/ui/Loader";
import { Dialog } from "@themes/pollinator/ui/Dialog";
import { TimezoneOption, useTimezoneOptions } from "@core/features/timezones";
import { getValidTimeslotsFromContiguousTimeslots } from "@core/features/timeslots";
import {
  useScheduling,
  useSubmitSchedulingAvailability,
  useGetSchedulingAvailability,
  START_HOUR,
  END_HOUR,
  MIN_CONTIGUOUS_TIMESLOT_DURATION,
  MIN_REQUIRED_HOURS,
} from "@core/features/scheduling";
import { useUpdateActionStatus, ACTION_STATUS } from "@core/features/actions";
import { usePlatformStore } from "@platform/store";
import { Scheduling } from "@platform/features/programScheduling";

/**
 * Generate 7 consecutive UTC dates starting from a given date or today.
 */
function generateWeekDates(startDate?: string): string[] {
  const start = startDate ? moment.utc(startDate) : moment.utc().startOf("week");
  const dates: string[] = [];
  for (let i = 0; i < 7; i++) {
    dates.push(start.clone().add(i, "days").format("YYYY-MM-DD"));
  }
  return dates;
}

export type Props = {
  onDone: () => void;
  onClose: () => void;
  isOpen: boolean;
  programId: number;
  userPhaseActionId: number;
  scheduledEngagementStartDate?: string;
};

export function SchedulingDialog({
  onDone,
  onClose,
  isOpen,
  programId,
  userPhaseActionId,
  scheduledEngagementStartDate,
}: Props) {
  const navigate = useNavigate();

  // Get user from store for timezone
  const user = usePlatformStore((s) => s.user);

  // client timezone reading and updating logic
  const { timezones, selectedTimezone, handleTimezoneChange } =
    useTimezoneOptions({ userTimezone: user?.timezone });

  // Generate UTC dates for the scheduling grid
  const utcDates = useMemo(
    () => generateWeekDates(scheduledEngagementStartDate),
    [scheduledEngagementStartDate]
  );

  // query api for saved time slots
  const {
    data: savedTimeSlots,
    isLoading,
    isError,
    refetch,
  } = useGetSchedulingAvailability(programId);

  // scheduling slot selection and grouping logic
  const {
    contiguousTimeslots,
    timeSlots,
    selectedTimeSlots,
    handleMouseDown,
    handleMouseOver,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    validHoursCount,
    requirementsMet,
    headers,
    columnCount,
    selectedOptionIds,
  } = useScheduling({
    utcDates,
    savedTimeSlots: savedTimeSlots || [],
    selectedTimezone: selectedTimezone || ({} as TimezoneOption),
    mode: "freeform",
    startHour: START_HOUR,
    endHour: END_HOUR,
    minRequiredHours: MIN_REQUIRED_HOURS,
    minContiguousDuration: MIN_CONTIGUOUS_TIMESLOT_DURATION,
    selectableOptions: [],
    requiredSelections: 0,
    headerFormat: "weekday",
  });

  // mutation to update phase action status
  const updateActionStatus = useUpdateActionStatus({
    actionId: userPhaseActionId,
    status: ACTION_STATUS.COMPLETED,
  });

  // mutation to submit scheduling availability to api
  const submitSchedulingAvailability = useSubmitSchedulingAvailability({
    programId,
    onSuccess: () => {
      updateActionStatus.mutate();
      onDone();
      refetch();
    },
  });

  // submission mutation state for ux feedback
  const { isPending, isError: isSubmitError } = submitSchedulingAvailability;

  // navigate to error page if there is an error
  useEffect(() => {
    if (isError || isSubmitError) {
      navigate("/error");
    }
  }, [isError, isSubmitError, navigate]);

  // submit handler cleans up the contiguous slots and submits to api
  const handleSubmit = () => {
    const validTimeSlots =
      getValidTimeslotsFromContiguousTimeslots(contiguousTimeslots);

    submitSchedulingAvailability.mutateAsync(validTimeSlots);
  };

  return (
    <Dialog
      title="Weekly Availability"
      description="Your participation in Pollinator is not confirmed until you choose at least 10 hours' worth of time slots when you will be available to meet with your peer circle this fall. To ensure you're successfully matched, we recommend choosing as many time slots as possible."
      isOpen={isOpen}
      onClose={onClose}
      className="py-0"
      containerClassName="gap-0"
      primaryButtonText="Save"
      secondaryButtonText="Cancel"
      onPrimaryClick={handleSubmit}
      onSecondaryClick={onClose}
      primaryButtonProps={{
        disabled: !requirementsMet || isPending,
      }}
      secondaryButtonProps={{
        disabled: isPending,
      }}
      size="5xl"
    >
      {isPending && <Loader isAbsolute />}
      <Scheduling
        timeZoneOptions={timezones}
        selectedTimezone={selectedTimezone || ({} as TimezoneOption)}
        onTimezoneChange={handleTimezoneChange}
        isSubmitting={isPending}
        isLoading={isLoading}
        timeSlots={timeSlots}
        selectedTimeSlots={selectedTimeSlots}
        contiguousTimeslots={contiguousTimeslots}
        handleMouseDown={handleMouseDown}
        handleMouseOver={handleMouseOver}
        handleTouchStart={handleTouchStart}
        handleTouchMove={handleTouchMove}
        handleTouchEnd={handleTouchEnd}
        validHoursCount={validHoursCount}
        mode="freeform"
        headers={headers}
        columnCount={columnCount}
        selectableOptions={[]}
        selectedOptionIds={selectedOptionIds}
        minRequiredHours={MIN_REQUIRED_HOURS}
        requiredSelections={0}
      />
    </Dialog>
  );
}

export default SchedulingDialog;
